import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { GridLayout } from 'shared/ui/grid-layout';
import { OkvedPartitionDto } from '@sr/dto';
import { Button, IconButton, TableRow, TableCell, Typography, Box, Stack } from '@mui/material';
import { useSnack } from 'shared/ui/snack';
import { ErrorBanner } from '../../../shared/ui/error-banner';
import { useDeleteOkvedPartition, useOkvedPartitionList } from './okved.hooks';
import { FormDialog } from 'shared/ui/form-dialog';
import { FormikErrors, FormikValues } from 'formik';
import OkvedPartitionForm from './okved-partition.form';
import { VALIDATION_REQUIRED_FIELD } from 'utils/form-tools';
import { okvedApi } from 'entities/okved/api-slice';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import useConfirmDialog from 'hooks/confirm-dialog.hook';

export interface FormValues {
  partitionCode: string | '';
  name: string | '';
}

export const validate = (values: FormValues) => {
  const errors: FormikErrors<FormValues> = {};
  if (!values.partitionCode) errors.partitionCode = VALIDATION_REQUIRED_FIELD;
  if (!values.name) errors.name = VALIDATION_REQUIRED_FIELD;

  return errors;
};

const initialValues: FormValues = {
  partitionCode: '',
  name: ''
};

interface OkvedPartitionListProps {
  onPartitionSelect: (id: number) => void;
  selectedPartitionId: number | null;
}

const columns = ['Раздел', 'Наименование раздела', ''];

const OkvedPartitionList = ({ onPartitionSelect, selectedPartitionId }: OkvedPartitionListProps) => {
  const { collection, error, isLoading } = useOkvedPartitionList();

  const [showAddDialog, setShowAddDialog] = useState(false);

  const { showSuccess, showError } = useSnack();
  const [addItem, { isError }] = okvedApi.useCreateOkvedPartitionMutation();

  const confirmDialog = useConfirmDialog();
  
  const deleteOkvedPartition = useDeleteOkvedPartition();

  if (error) {
    return <ErrorBanner errorMessage={error} />;
  }

  const handleAddOkvedPartition = async (values: FormikValues) => {
    await addItem({
      partitionCode: values.partitionCode,
      name: values.name
    })
      .unwrap()
      .then(() => {
        showSuccess('Запись успешно добавлена');
        setShowAddDialog(false);
      })
      .catch((e) => showError(`Ошибка записи: ${extractNestJsErrorMessage(e)}`));
  };
    
  const handleDelete = (id: number) => {
    confirmDialog({ content: 'При удалении раздела затрутся все группы. Удалить раздел ОКВЭД?', title: 'Требуется подтверждение' })
      .then(() => deleteOkvedPartition(id)
        .then(() => showSuccess('Раздел ОКВЭД удалён'))
        .catch((e) => showError('Ошибка удаления раздела ОКВЭД', extractNestJsErrorMessage(e))));

  };

  return (
    <Box>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)}>
              Добавить
      </Button>
      
      <GridLayout<OkvedPartitionDto>
        columns={columns}
        items={collection}
        isLoading={isLoading}
        error={error}
        itemRender={(okvedPartition) => (
          <Row
            key={okvedPartition.id}
            okved={okvedPartition}
            isSelected={selectedPartitionId === okvedPartition.id}
            onSelect={() => onPartitionSelect(okvedPartition.id)}
            onDeleteClick={() => handleDelete(okvedPartition.id)}
          />
        )}
        noItemsText="Список разделов пуст"
      />
      <FormDialog
        initialValues={initialValues}
        renderForm={() => (
          <Box minWidth={450} pb={1}>
            <Stack spacing={2} direction="column">
              <OkvedPartitionForm />
            </Stack>
          </Box>
        )}
        renderTitle="Новая раздел ОКВЭД"
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={handleAddOkvedPartition}
        validate={validate}
        dialogProps={{
          fullWidth: true
        }}
      />
    </Box>
  );
};

type Props = {
  okved: OkvedPartitionDto;
  onSelect: () => void;
  isSelected: boolean;
  onDeleteClick: () => void;
};

const Row = ({ okved, onSelect, isSelected, onDeleteClick }: Props) => {
  return (
    <TableRow onClick={onSelect} sx={{ 
      cursor: 'pointer', 
      backgroundColor: isSelected ? '#e3f2fd' : 'inherit',
      '&:hover': { 
        backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5' 
      }
    }}>
      <TableCell>
        <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
          {okved.partitionCode}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
          {okved.name}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton edge="end" onClick={onDeleteClick}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default OkvedPartitionList;
